// src/features/tuition/components/students/StudentManagement.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  PersonOff as DeactivateIcon,
  PersonAdd as ActivateIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import StudentList from './StudentList';
import StudentDetail from './StudentDetail';
import StudentForm from './StudentForm';
import { 
  fetchStudents, 
  createStudent, 
  updateStudent, 
  deleteStudent,
  updateStudentStatus
} from '../../services/studentsService';

const StudentManagement = ({ userId }) => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'detail'
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    loadStudents();
  }, [userId]);
  
  useEffect(() => {
    filterStudents();
  }, [students, searchTerm]);
  
  const loadStudents = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const studentsData = await fetchStudents(userId);
      setStudents(studentsData);
      setFilteredStudents(studentsData);
    } catch (error) {
      console.error("Error loading students:", error);
      setNotification({
        open: true,
        message: `Error loading students: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const filterStudents = () => {
    if (!searchTerm.trim()) {
      setFilteredStudents(students);
      return;
    }
    
    const searchTermLower = searchTerm.toLowerCase();
    const filtered = students.filter(student => 
      student.firstName.toLowerCase().includes(searchTermLower) ||
      student.lastName.toLowerCase().includes(searchTermLower) ||
      `${student.firstName} ${student.lastName}`.toLowerCase().includes(searchTermLower) ||
      (student.school && student.school.toLowerCase().includes(searchTermLower)) ||
      (student.grade && `grade ${student.grade}`.toLowerCase().includes(searchTermLower))
    );
    
    setFilteredStudents(filtered);
  };
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleAddStudent = () => {
    setCurrentStudent(null);
    setIsFormOpen(true);
  };
  
  const handleEditStudent = (student) => {
    setCurrentStudent(student);
    setIsFormOpen(true);
    handleMenuClose();
  };
  
  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setViewMode('detail');
  };
  
  const handleBackToList = () => {
    setViewMode('list');
    setSelectedStudent(null);
  };
  
  const handleFormClose = () => {
    setIsFormOpen(false);
    setCurrentStudent(null);
  };
  
  const handleMenuOpen = (event, studentId) => {
    setAnchorEl(event.currentTarget);
    setSelectedStudentId(studentId);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedStudentId(null);
  };
  
  const confirmDeleteStudent = () => {
    setDeleteConfirmOpen(true);
    handleMenuClose();
  };
  
  const closeDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
  };
  
  const handleFormSubmit = async (studentData) => {
    try {
      if (currentStudent) {
        // Update existing student
        const updatedStudent = await updateStudent(currentStudent.id, studentData);
        setStudents(students.map(s => 
          s.id === currentStudent.id ? updatedStudent : s
        ));
        
        // Update the selected student if in detail view
        if (selectedStudent && selectedStudent.id === currentStudent.id) {
          setSelectedStudent(updatedStudent);
        }
        
        setNotification({
          open: true,
          message: "Student updated successfully",
          severity: "success"
        });
      } else {
        // Add new student
        const newStudent = await createStudent(userId, studentData);
        setStudents([newStudent, ...students]);
        
        setNotification({
          open: true,
          message: "Student added successfully",
          severity: "success"
        });
      }
      
      handleFormClose();
    } catch (error) {
      console.error("Error saving student:", error);
      setNotification({
        open: true,
        message: `Error: ${error.message}`,
        severity: "error"
      });
    }
  };
  
  const handleDeleteStudent = async () => {
    if (!selectedStudentId) return;
    
    try {
      await deleteStudent(selectedStudentId);
      setStudents(students.filter(s => s.id !== selectedStudentId));
      
      // If the deleted student is the currently selected one, go back to list view
      if (selectedStudent && selectedStudent.id === selectedStudentId) {
        handleBackToList();
      }
      
      setNotification({
        open: true,
        message: "Student deleted successfully",
        severity: "success"
      });
    } catch (error) {
      console.error("Error deleting student:", error);
      setNotification({
        open: true,
        message: `Error: ${error.message}`,
        severity: "error"
      });
    } finally {
      closeDeleteConfirm();
    }
  };
  
  const handleToggleStudentStatus = async () => {
    if (!selectedStudentId) return;
    
    const student = students.find(s => s.id === selectedStudentId);
    if (!student) return;
    
    const newStatus = student.status === 'active' ? 'inactive' : 'active';
    
    try {
      await updateStudentStatus(selectedStudentId, newStatus);
      
      // Update the student in the state
      setStudents(students.map(s => 
        s.id === selectedStudentId ? { ...s, status: newStatus } : s
      ));
      
      // Update the selected student if in detail view
      if (selectedStudent && selectedStudent.id === selectedStudentId) {
        setSelectedStudent({ ...selectedStudent, status: newStatus });
      }
      
      setNotification({
        open: true,
        message: `Student ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`,
        severity: "success"
      });
    } catch (error) {
      console.error("Error updating student status:", error);
      setNotification({
        open: true,
        message: `Error: ${error.message}`,
        severity: "error"
      });
    } finally {
      handleMenuClose();
    }
  };
  
  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };
  
  if (loading && students.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress sx={{ color: THEME_COLORS.primary }} />
      </Box>
    );
  }
  
  return (
    <Box>
      {viewMode === 'list' ? (
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" fontWeight="bold">
              Students
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddStudent}
              sx={{
                backgroundColor: THEME_COLORS.primary,
                '&:hover': { backgroundColor: '#1e1c4e' }
              }}
            >
              Add Student
            </Button>
          </Box>
          
          <Box mb={3}>
            <TextField
              fullWidth
              placeholder="Search students..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton 
                      edge="end" 
                      onClick={() => setSearchTerm('')}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ maxWidth: 400 }}
            />
          </Box>
          
          <StudentList 
            students={filteredStudents}
            loading={loading}
            onAddStudent={handleAddStudent}
            onViewStudent={handleViewStudent}
            onMenuOpen={handleMenuOpen}
          />
        </Paper>
      ) : (
        <StudentDetail 
          student={selectedStudent}
          onBack={handleBackToList}
          onEdit={() => handleEditStudent(selectedStudent)}
          onMenuOpen={handleMenuOpen}
        />
      )}
      
      {/* Student Form Dialog */}
      <StudentForm
        open={isFormOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        initialData={currentStudent}
      />
      
      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          const student = students.find(s => s.id === selectedStudentId);
          if (student) handleEditStudent(student);
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Student</ListItemText>
        </MenuItem>
        
        {selectedStudentId && (
          <MenuItem onClick={handleToggleStudentStatus}>
            <ListItemIcon>
              {students.find(s => s.id === selectedStudentId)?.status === 'active' ? (
                <DeactivateIcon fontSize="small" />
              ) : (
                <ActivateIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>
              {students.find(s => s.id === selectedStudentId)?.status === 'active' ? 
                'Deactivate Student' : 'Activate Student'}
            </ListItemText>
          </MenuItem>
        )}
        
        <MenuItem onClick={confirmDeleteStudent} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Student</ListItemText>
        </MenuItem>
      </Menu>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={closeDeleteConfirm}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this student? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm}>Cancel</Button>
          <Button onClick={handleDeleteStudent} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentManagement;